import React, { useState, useCallback, useMemo } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import Select from '@atlaskit/select';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import {
	FRAGMENT_FIELD_OPTIONS_FIRST,
	SEARCH_DEBOUNCE_TIMEOUT,
} from '@atlassian/jira-issue-field-constants';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base';
import { useSuspenselessRefetch } from '@atlassian/jira-issue-hooks/src/services/use-suspenseless-refetch';

import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';

import type { singleSelect_issueFieldSingleSelectEditviewFull_SingleSelectEditViewWithFieldOptionsFragment$key as SingleSelectEditViewKey } from '@atlassian/jira-relay/src/__generated__/singleSelect_issueFieldSingleSelectEditviewFull_SingleSelectEditViewWithFieldOptionsFragment.graphql';
import singleSelectFieldOptions_singleSelectEditViewQuery, {
	type JiraFieldOptionIdsFilterInput,
	type singleSelectFieldOptions_singleSelectEditView$variables as SingleSelectEditViewQueryTypeVariables,
	type singleSelectFieldOptions_singleSelectEditView as SingleSelectEditViewQueryType,
} from '@atlassian/jira-relay/src/__generated__/singleSelectFieldOptions_singleSelectEditView.graphql';
import messages from './messages';
import type {
	SingleSelectEditViewWithFieldOptionsFragmentProps,
	Option,
	SingleSelectEditViewProps,
} from './types';

import { getOptionsWithoutSelected } from './utils';

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
const filterOptions = (option: Option, query: string | void): boolean =>
	(typeof option.value === 'string' &&
		option.value.toLowerCase().includes((query || '').toLowerCase())) ||
	(typeof option.label === 'string' &&
		option.label.toLowerCase().includes((query || '').toLowerCase()));

const getSuggestionsVariables = (
	id: string,
	queryString: string,
	filterOptionsById: JiraFieldOptionIdsFilterInput | null,
	first?: number,
): SingleSelectEditViewQueryTypeVariables => ({
	id,
	searchBy: queryString || '',
	filterById: filterOptionsById,
	first,
});

/**
 * SingleSelectEditViewWithFieldOptionsFragment is a version of the edit view that allows
 * the passing of a fragment for experiences like GIC that might want to group fetch data on mount
 * instead of having a separate network request for it
 *
 * @param props {@link SingleSelectEditViewWithFieldOptionsFragmentProps}
 */
export const SingleSelectEditViewWithFieldOptionsFragment = ({
	fieldId,
	fieldOptionsFragmentRef,
	value,
	fieldName = '',
	onChange,
	isDisabled = false,
	isInvalid = false,
	filterOptionsById = null,
	autoFocus = false,
	spacing = 'default',
	openMenuOnFocus,
}: SingleSelectEditViewWithFieldOptionsFragmentProps) => {
	const { formatMessage } = useIntl();
	const [searchByString, setSearchByString] = useState<string>('');

	// vvv --- Relay stuff --- vvv
	const { data: fieldOptionsData, refetch: refetchSuggestions } = usePaginationFragment<
		SingleSelectEditViewQueryType,
		SingleSelectEditViewKey
	>(
		graphql`
			fragment singleSelect_issueFieldSingleSelectEditviewFull_SingleSelectEditViewWithFieldOptionsFragment on Query
			@refetchable(queryName: "singleSelectFieldOptions_singleSelectEditView")
			@argumentDefinitions(
				id: { type: "ID!" }
				searchBy: { type: "String", defaultValue: "" }
				first: { type: "Int", defaultValue: 100 }
				after: { type: "String", defaultValue: null }
				filterById: { type: "JiraFieldOptionIdsFilterInput" }
			) {
				node(id: $id) {
					... on JiraSingleSelectField {
						fieldOptions(
							searchBy: $searchBy
							first: $first
							after: $after
							filterById: $filterById
						) @connection(key: "main_issueFieldSingleSelectEditviewFull__fieldOptions") {
							edges {
								node {
									value: id
									label: value
									optionId
									isDisabled
								}
							}
						}
					}
				}
			}
		`,
		fieldOptionsFragmentRef,
	);

	const [refetchAndPropCallback, isLoading, lastFetchError] = useSuspenselessRefetch<
		SingleSelectEditViewQueryType,
		SingleSelectEditViewKey
	>(singleSelectFieldOptions_singleSelectEditViewQuery, refetchSuggestions);

	const [debouncedSuggestionsRefetcher] = useDebouncedCallback(
		refetchAndPropCallback,
		SEARCH_DEBOUNCE_TIMEOUT,
	);

	const [leadingEdgeDebouncedSuggestionsRefetcher] = useDebouncedCallback(
		refetchAndPropCallback,
		SEARCH_DEBOUNCE_TIMEOUT,
		{ leading: true },
	);

	const onFocus = useCallback(() => {
		leadingEdgeDebouncedSuggestionsRefetcher(
			getSuggestionsVariables(
				fieldId,
				searchByString,
				filterOptionsById,
				FRAGMENT_FIELD_OPTIONS_FIRST,
			),
		);
	}, [leadingEdgeDebouncedSuggestionsRefetcher, fieldId, searchByString, filterOptionsById]);

	const onSearchByStringChangeFunction = useCallback(
		(newSearchByString: string): void => {
			setSearchByString(newSearchByString);
			debouncedSuggestionsRefetcher(
				getSuggestionsVariables(
					fieldId,
					newSearchByString,
					filterOptionsById,
					FRAGMENT_FIELD_OPTIONS_FIRST,
				),
			);
		},
		[debouncedSuggestionsRefetcher, fieldId, filterOptionsById],
	);

	const options: Option[] = useMemo(
		() =>
			lastFetchError === null
				? fieldOptionsData?.node?.fieldOptions?.edges
						?.map((edge) => edge?.node)
						.filter(Boolean)
						.map(({ label, value: optionValue, optionId, isDisabled: isOptionDisabled }) => ({
							label: label ?? '',
							value: optionValue,
							optionId,
							isDisabled: isOptionDisabled,
						})) ?? []
				: [
						{
							label: formatMessage(messages.failedFetch),
							value: '',
							optionId: '',
							isDisabled: true,
						},
					],
		[fieldOptionsData?.node?.fieldOptions?.edges, lastFetchError, formatMessage],
	);

	return (
		<Select
			isInvalid={isInvalid}
			isDisabled={isDisabled}
			options={getOptionsWithoutSelected(options, value)}
			onFocus={onFocus}
			isLoading={isLoading}
			onInputChange={onSearchByStringChangeFunction}
			filterOption={filterOptions}
			loadingMessage={() => formatMessage(messages.loading)}
			noOptionsMessage={() => formatMessage(messages.empty)}
			value={value}
			placeholder={formatMessage(messages.placeholder, {
				fieldName,
			})}
			onChange={onChange}
			autoFocus={autoFocus}
			menuPosition="fixed"
			isClearable
			styles={defaultSelectStyles}
			spacing={spacing}
			openMenuOnFocus={ff('select-field-dropdown-opening-issue_oke9p') ? openMenuOnFocus : true}
		/>
	);
};

/**
 * Simple-usecase variant which handles its own fetching of suggestions data.
 * Use the SingleSelectEditViewWithFieldOptionsFragment variant if you want to handle
 * prefilling the suggestions data as part of a larger query instead of on mount / on focus.
 *
 * @param props {@link SingleSelectEditViewProps}
 */
export const SingleSelectEditView = (props: SingleSelectEditViewProps) => {
	const suggestionsData = useLazyLoadQuery<SingleSelectEditViewQueryType>(
		graphql`
			query singleSelect_SingleSelect_suggestionsQuery(
				$id: ID!
				$filterById: JiraFieldOptionIdsFilterInput
			) {
				...singleSelect_issueFieldSingleSelectEditviewFull_SingleSelectEditViewWithFieldOptionsFragment
					@arguments(id: $id, searchBy: "", filterById: $filterById)
			}
		`,
		{ id: props.fieldId, filterById: props.filterOptionsById },
		{ fetchPolicy: 'store-only' },
	);

	return (
		<SingleSelectEditViewWithFieldOptionsFragment
			{...props}
			fieldOptionsFragmentRef={suggestionsData}
		/>
	);
};
